import { getNominative } from 'common/utils'
import { CarerRole, Home } from '@lmc/ts-common'
import { Icon, IconType } from '@lmc/ui-kit'
import { riskAssessmentReportConstants } from 'locale/riskAssessmentReports'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import useFeatureFlags from '../../hooks/useFeatureFlags'
import { MenuItemType } from 'antd/lib/menu/hooks/useItems'

export const getNavigationTabs = (
  features: Home | undefined,
  role: CarerRole | string,
  carePlanNominative?: string,
  downloadEnabled?: boolean,
  isPrivacyOfficerView?: boolean,
  incidentReportsListViewEnabled?: boolean,
  incidentsDashboardEnabled?: boolean,
  hasRolePermissionForDownloadCentre?: boolean
) => {
  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENT_REPORT)
  const flags = useFeatureFlags({
    flagNames: ['outcomes_and_goals_v2', 'rota_overview_report'],
  })?.data

  const skillsAndNeedsRoles = [
    CarerRole.careHomeAdmin,
    CarerRole.groupAdmin,
    CarerRole.lmcAdmin,
  ]

  if (!features) {
    return []
  }

  const matrixEnabled =
    features?.features?.skillsAndNeeds?.enabled &&
    skillsAndNeedsRoles.includes(role as CarerRole)

  const emarEnabled = features?.features?.eMAR?.enabled

  const proEnabled = features?.features?.pro?.enabled

  const emarStockEnabled = features?.features?.eMARStock?.enabled
  const goalsFlagEnabled =
    features?.features?.goals?.enabled && flags?.outcomes_and_goals_v2?.enabled
  const rotaOverviewReportEnabled =
    features?.features?.rota?.enabled && flags?.rota_overview_report?.enabled

  const carePlanText = getNominative(carePlanNominative, 'Care Plan')

  const NAV_ITEMS = [
    {
      url: 'coronavirus',
      label: 'Coronavirus',
      icon: <Icon customIcon={IconType.ReportsCoronavirus} />,
    },
    {
      url: 'incidents-route',
      label: 'Incidents',
      icon: <Icon customIcon={IconType.ReportsIncidents} />,
      subItems: [
        ...(proEnabled && incidentsDashboardEnabled
          ? [
              {
                url: 'incidents/dashboard',
                label: 'Incidents dashboard',
              },
            ]
          : []),
        ...(proEnabled && incidentReportsListViewEnabled
          ? [
              {
                url: 'incidents/list',
                label: 'Incidents list',
              },
            ]
          : []),
        {
          url: 'incidents',
          label: 'Incidents per month',
        },
      ],
    },
    {
      url: 'handovers/current',
      label: 'Current Handover',
      icon: <Icon customIcon={IconType.ReportsHandover} />,
    },
    {
      url: 'handovers/history',
      label: 'Handover History',
      icon: <Icon customIcon={IconType.ReportsHistory} />,
    },
    {
      url: 'occupancy-report',
      label: 'Occupancy Report',
      icon: <Icon customIcon={IconType.Home} />,
    },

    ...(features?.features?.pro?.enabled
      ? [
          {
            label: 'Monitoring Reports',
            url: 'monitoring-route',
            icon: <Icon customIcon={IconType.SideMonitoring} />,
            subItems: [
              ...(features.features?.reportsFluids?.enabled
                ? [
                    {
                      url: 'monitoring/fluids',
                      label: 'Fluids',
                      icon: <Icon customIcon={IconType.SidePlanning} />,
                    },
                  ]
                : []),
              ...(features.features?.reportsFood?.enabled
                ? [
                    {
                      url: 'monitoring/meals',
                      label: 'Food',
                      icon: <Icon customIcon={IconType.SidePlanning} />,
                    },
                  ]
                : []),
              ...(features.features?.reportsRepositionings?.enabled
                ? [
                    {
                      url: 'monitoring/turns',
                      label: 'Re-positionings',
                      icon: <Icon customIcon={IconType.SidePlanning} />,
                    },
                  ]
                : []),
              ...(features.features?.reportsWeight?.enabled
                ? [
                    {
                      url: 'monitoring/weight',
                      label: 'Weight',
                      icon: <Icon customIcon={IconType.SidePlanning} />,
                    },
                  ]
                : []),
              ...(features.features?.reportsSocial?.enabled
                ? [
                    {
                      url: 'monitoring/activity',
                      label: 'Social Activities',
                      icon: <Icon customIcon={IconType.SidePlanning} />,
                    },
                  ]
                : []),

              ...(features.features?.reportsMood?.enabled
                ? [
                    {
                      url: 'monitoring/mood',
                      label: 'Mood',
                      icon: <Icon customIcon={IconType.SidePlanning} />,
                    },
                  ]
                : []),

              ...(features.features?.reportsTemperature?.enabled
                ? [
                    {
                      url: 'monitoring/temperature',
                      label: 'Temperature',
                      icon: <Icon customIcon={IconType.SidePlanning} />,
                    },
                  ]
                : []),
              ...(features.features?.reportsWash?.enabled
                ? [
                    {
                      url: 'monitoring/wash',
                      label: 'Wash',
                      icon: <Icon customIcon={IconType.SidePlanning} />,
                    },
                  ]
                : []),

              ...(features.features?.reportsCustom?.enabled
                ? [
                    {
                      url: 'monitoring/custom',
                      label: 'Custom',
                      icon: <Icon customIcon={IconType.SidePlanning} />,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),

    ...(!features?.features?.pro?.enabled
      ? [
          {
            label: 'Monitoring Reports',
            url: 'monitoring-route',
            icon: <Icon customIcon={IconType.SideMonitoring} />,
            subItems: [
              {
                url: 'monitoring/fluids',
                label: 'Fluids',
                icon: <Icon customIcon={IconType.SidePlanning} />,
              },

              {
                url: 'monitoring/meals',
                label: 'Food',
                icon: <Icon customIcon={IconType.SidePlanning} />,
              },
            ],
          },
        ]
      : []),

    ...(features.pro
      ? [
          {
            label: t(riskAssessmentReportConstants.CARE_PLANNING, {
              carePlanText,
            }),
            url: 'care-planning-route',
            icon: <Icon customIcon={IconType.SidePlanning} />,
            subItems: [
              {
                url: 'care-planning/care-plans',
                label: getNominative(carePlanNominative, 'Care Plans'),
                icon: <Icon customIcon={IconType.SidePlanning} />,
              },
              {
                url: 'care-planning/risk-assessments',
                label: 'Risk Assessment Scores',
                icon: <Icon customIcon={IconType.SidePlanning} />,
              },
            ],
          },
        ]
      : [
          {
            url: 'care-plans',
            label: `${getNominative(carePlanNominative, 'Care Plans')}`,
            icon: <Icon customIcon={IconType.SidePlanning} />,
          },
        ]),
    ...(goalsFlagEnabled
      ? [
          {
            label: 'Goals',
            url: 'goals',
            icon: <Icon customIcon={IconType.ReportsGoals} />,
            subItems: [
              {
                url: 'goals/dashboard',
                label: 'Goals Dashboard',
              },
              {
                url: 'goals/goals-management',
                label: 'Goals Management',
              },
            ],
          },
        ]
      : []),
    ...(matrixEnabled
      ? [
          {
            url: 'training-matrix',
            label: 'Training and skills matrix',
            icon: <Icon customIcon={IconType.ReportsMatrix} />,
          },
        ]
      : []),
    ...(emarEnabled
      ? [
          {
            label: 'eMAR Reports',
            url: 'emar-route',
            icon: <Icon customIcon={IconType.SideMedication} />,
            subItems: emarStockEnabled
              ? [
                  { url: 'emar/medication-rounds', label: 'Medication Rounds' },
                  {
                    url: 'emar/stock-discrepancies',
                    label: 'Stock Discrepancies',
                  },
                  { url: 'emar/stock-levels', label: 'Stock Levels' },
                ]
              : [{ url: 'emar/medication-rounds', label: 'Medication Rounds' }],
          },
        ]
      : []),
    {
      url: 'cqi',
      label: 'CQI',
      icon: <Icon customIcon={IconType.ReportsCQI} />,
    },
    {
      url: 'care-delivery',
      label: 'Care Delivery',
      icon: <Icon customIcon={IconType.ReportsDelivery} />,
    },
    ...(downloadEnabled || hasRolePermissionForDownloadCentre
      ? [
          {
            url: 'download-centre',
            label: 'Download Centre',
            icon: <Icon customIcon={IconType.DownloadIcon} />,
          },
        ]
      : []),

    ...(rotaOverviewReportEnabled
      ? [
          {
            url: 'rostering',
            label: 'Rostering',
            icon: <Icon customIcon={IconType.Rostering} />,
            subItems: [
              {
                url: 'rostering/rota-overview',
                label: 'Rota Overview Report',
              },
            ],
          },
        ]
      : []),
    ...(isPrivacyOfficerView
      ? [
          {
            url: 'audit-trail',
            label: 'Audit Trail',
            icon: <Icon customIcon={IconType.AuditIcon} />,
          },
        ]
      : []),
  ]

  return NAV_ITEMS as unknown as MenuItemType[]
}
